<template lang="pug">
.questionnaire-table-component
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    //- ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    //- レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    //- フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    //- セル：状態
    template(#cell(status)="row")
      .d-flex.flex-row.align-items-center.justify-content-center
        span.non-sended.ml-2.mr-2(v-if="!row.item.sendDatetime") 未送信
        AcceptStatusSwitch.accept-switch(v-if="row.item.sendDatetime" v-model="row.item.acceptingAnswers" @onChange="onAcceptingAnswers(row.item)")
    //- セル：アンケート期間
    template(#cell(terms)="row")
      .d-flex.flex-column.align-items-center.justify-content-center(v-if="row.item.sendDatetime")
        span {{ getTermStart(row.item) }} ～
        span {{ getTermEnd(row.item) }}
    //- セル：タイトル
    template(#cell(title)="row")
      span.font-weight-bold {{ row.item.title }}
    //- セル：宛先
    template(#cell(targets)="row")
      span {{ getTargets(row.item) }}
    //- セル：回答率
    template(#cell(responseRate)="row")
      .d-flex.flex-row.align-items-center.justify-content-center
        .response-rate(v-if="row.item.responseCount != null && row.item.targetsCount != null && row.item.targetsCount != 0")
          span {{ calculateResponseRate(row.item) }}
          span （{{ row.item.responseCount }}／{{ row.item.targetsCount }}人）
    //- セル：回答結果
    template(#cell(answersList)="row")
      button.btn.answers-list-btn(v-if="row.item.sendDatetime" @click="onDetail(row.item)" type="button") 確認
    //- セル：操作
    template(#cell(operation)="row")
      .control-buttons
        button.btn.edit-btn.mr-2(@click="onEdit(row.item)" type="button") 修正
        button.btn.delete-btn.mr-2(@click="onDelete(row.item)" type="button") 削除
        button.btn.copy-btn(@click="onCopy(row.item)" type="button") コピー
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { QuestionnairesGetModel, TargetType } from "@/commons/services/Questionnaires";
import AcceptStatusSwitch from "@/pc/containers/questionnaires/AcceptStatusSwitch.vue";

/**
 * ### 部屋テーブルコンポーネント
 */
@Component({
  components: {
    AcceptStatusSwitch
  }
})
export default class QuestionnaireTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<QuestionnaireTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<QuestionnaireTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<QuestionnaireTableItem> {
    return this.value;
  }

  /**
   * ### アンケート期間（開始）を返す
   */
  public getTermStart(item: QuestionnaireTableItem): string {
    if (!item.sendDatetime) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(item.sendDatetime, "yyyy年mm月dd日 hh:MM");
  }

  /**
   * ### アンケート期間（終了）を返す
   */
  public getTermEnd(item: QuestionnaireTableItem): string {
    // 受付中
    if (item.acceptingAnswers == true) {
      // 期限が未設定なら文字列を返す
      if (!item.replyLimit) {
        return "無期限";
      }
      // 期限があるなら期限を返す
      else {
        return this.$dateUtil.convertNumberToString(item.replyLimit, "yyyy年mm月dd日 hh:MM");
      }
    }
    // 受付終了
    else {
      // ありえないが、期限が設定されていない場合は空文字を返す
      if (!item.replyLimit) {
        return "";
      }
      // 期限を返す
      return this.$dateUtil.convertNumberToString(item.replyLimit, "yyyy年mm月dd日 hh:MM");
    }
  }

  /**
   * ### 宛先の文字列を返す
   */
  public getTargets(item: QuestionnaireTableItem): string {
    switch (item.targetType) {
      case TargetType.ALL_FAMILIES: {
        return "家族全員";
      }
      case TargetType.FAMILIES: {
        return "家族個別";
      }
      case TargetType.OTHER_USERS: {
        return "施設関係者個別";
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of item.sendMessageGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      case TargetType.RESIDENT_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of item.residentGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      case TargetType.OTHER_USER_GROUPS: {
        const groupNames: string[] = [];
        for (const groupInfo of item.otherUserGroupInfos) {
          groupNames.push(groupInfo.name);
        }
        return groupNames.join("、");
      }
      default: {
        return "";
      }
    }
  }

  public calculateResponseRate(item: QuestionnaireTableItem): string {
    if (item.responseCount != null && item.targetsCount != null) {
      const percentage: number = ((item.responseCount / item.targetsCount) * 10000) / 100;
      return percentage.toFixed(0) + "％";
    } else {
      return "";
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: QuestionnaireTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(item, filter);
  }

  public filterSearchWords(item: QuestionnaireTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let found: boolean = true;
    for (const word of words) {
      if (item.title.indexOf(word) == -1) {
        found = false;
      }
    }

    return found;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  public onAcceptingAnswers(item: QuestionnaireTableItem): void {
    this.$emit("onAcceptingAnswers", item);
    return;
  }

  /**
   * ### 回答結果 確認ボタン押下イベント
   */
  public onDetail(item: QuestionnaireTableItem): void {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: QuestionnaireTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: QuestionnaireTableItem): void {
    this.$emit("onDelete", item);
    return;
  }

  /**
   * ### コピーボタン押下イベント
   */
  public onCopy(item: QuestionnaireTableItem): void {
    this.$emit("onCopy", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface QuestionnaireTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface QuestionnaireTableItem extends QuestionnairesGetModel {
  /** 回答者数 */
  responseCount: number | null;
  /** 未回答者数 */
  targetsCount: number | null;
}

export interface FilterSetting {
  searchWord: string;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.questionnaire-table-component {
  .non-sended {
    color: $font-strong-color;
    font-weight: bold;
  }

  .answers-list-btn,
  .edit-btn,
  .delete-btn {
    width: 64px;
  }
  .copy-btn {
    width: 80px;
  }
}
</style>
